// Verwaltung (nur GQL): Gesamtauswertung für die Ersteinschätzung – Reife je Standort, Stufe-1-Bedingung,
// Benchmark je Konzessionsgebiet, erster nicht verjährter Zahlungsmonat je Standort (Rechenhilfe).
import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import type { DbPortalAuswertung } from '@gql/shared-types';
import { api, API_BASIS } from '../api/client.js';
import { useAuth } from '../auth/AuthKontext.js';
import { AnnahmenFormular } from '../components/AnnahmenFormular.js';
import { VerwaltungReiter } from '../components/VerwaltungReiter.js';
import { annahmenAbfrage, monatText, REIFE_TEXT, standardAnnahmen, type Annahmen } from '../daten/analyse.js';
import { euro, zeitpunkt } from '../daten/lieferungen.js';

export function VerwaltungAuswertung() {
  const { zustand } = useAuth();
  const admin = zustand.status === 'bereit' && zustand.ich.nutzer?.rolle === 'gql_admin';
  const [annahmen, setAnnahmen] = useState<Annahmen>(standardAnnahmen);
  const [w, setW] = useState<DbPortalAuswertung | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [offen, setOffen] = useState<string | null>(null);
  const abfrage = useMemo(() => annahmenAbfrage(annahmen), [annahmen]);

  useEffect(() => {
    if (!admin || (annahmen.kenntnis === 'datum' && !annahmen.kenntnisDatum)) return;
    let aktiv = true;
    api<DbPortalAuswertung>(`/admin/auswertung${abfrage ? `?${abfrage}` : ''}`)
      .then((d) => {
        if (!aktiv) return;
        setW(d);
        setFehler(null);
      })
      .catch((f: Error) => aktiv && setFehler(f.message));
    return () => {
      aktiv = false;
    };
  }, [admin, abfrage, annahmen.kenntnis, annahmen.kenntnisDatum]);

  if (!admin) return <Navigate to="/standorte" replace />;
  return (
    <div className="seite">
      <div className="titelzeile">
        <h1>Auswertung für die Ersteinschätzung</h1>
        <a href={`${API_BASIS}/export.xlsx`} className="knopf-sekundaer knopf-auto" download>
          Alles als Excel herunterladen
        </a>
      </div>
      <VerwaltungReiter />
      <p className="hinweis-klein">Rechenhilfe mit den eingestellten Annahmen – keine Rechtsauskunft. Kennwert = Median der gesicherten Monatsentgelte im Kernzeitraum; Benchmark = Median der Kennwerte je Konzessionsgebiet.</p>
      <AnnahmenFormular wert={annahmen} onChange={setAnnahmen} mitVergleichswert={false} />
      {fehler && (
        <p className="fehlertext" role="alert">
          {fehler}
        </p>
      )}
      {!w && !fehler && (
        <p role="status">
          <span className="spinner" aria-hidden="true" /> Auswertung wird berechnet …
        </p>
      )}
      {w && (
        <>
          <div className="kennzahlen">
            <div className="kennzahl">
              <span className="kennzahl-label">Kurz-Einschätzung (Stufe 1)</span>
              <span className={`kennzahl-wert reife reife-${w.reife.stufe1.erreicht ? 'bereit' : 'teilweise'}`}>{w.reife.stufe1.erreicht ? 'Bedingung erfüllt' : 'noch nicht erreichbar'}</span>
              <span className="zelle-hinweis">
                {w.reife.stufe1.standorteBereit} Standorte bereit in {w.reife.stufe1.gebieteBereit} Gebieten – {w.reife.stufe1.bedingung}
              </span>
            </div>
            <div className="kennzahl">
              <span className="kennzahl-label">Einschätzungsreife</span>
              <span className="kennzahl-wert">
                {w.reife.bereit} bereit · {w.reife.teilweise} teilweise · {w.reife.unzureichend} unzureichend
              </span>
            </div>
            <div className="kennzahl">
              <span className="kennzahl-label">Monatsentgelt netto über alle Standorte</span>
              <span className="kennzahl-wert">{w.gesamt.medianCent !== null ? `Median ${euro(w.gesamt.medianCent)}` : '–'}</span>
              <span className="zelle-hinweis">
                {w.gesamt.mitKennwert} Standorte mit Kennwert{w.gesamt.minCent !== null && ` · ${euro(w.gesamt.minCent)} bis ${euro(w.gesamt.maxCent!)}`}
              </span>
            </div>
          </div>

          <h2>Benchmark je Konzessionsgebiet</h2>
          <div className="tabelle-rahmen">
            <table className="tabelle tabelle-karten benchmark">
              <caption className="sr-only">Benchmark je Konzessionsgebiet</caption>
              <thead>
                <tr>
                  <th scope="col">Gebiet</th>
                  <th scope="col">Vergleichbarkeit</th>
                  <th scope="col">Standorte</th>
                  <th scope="col">niedrigstes</th>
                  <th scope="col">Median</th>
                  <th scope="col">höchstes</th>
                  <th scope="col"></th>
                </tr>
              </thead>
              <tbody>
                {w.gebiete.map((g) => {
                  const schluessel = g.gebietSlug ?? 'ohne';
                  const auf = offen === schluessel;
                  return (
                    <GebietZeilen key={schluessel} g={g} auf={auf} umschalten={() => setOffen(auf ? null : schluessel)} />
                  );
                })}
              </tbody>
            </table>
          </div>

          <h2>Annahmen</h2>
          <ul className="annahmen-liste">
            {w.annahmen.map((x) => (
              <li key={x}>{x}</li>
            ))}
          </ul>
          <p className="zelle-hinweis">Stand {zeitpunkt(w.stand)}</p>
        </>
      )}
    </div>
  );
}

function GebietZeilen({ g, auf, umschalten }: { g: DbPortalAuswertung['gebiete'][number]; auf: boolean; umschalten: () => void }) {
  return (
    <>
      <tr>
        <td data-label="Gebiet">{g.gebietSlug ? <Link to={`/gebiet/${g.gebietSlug}`}>{g.gebietName}</Link> : g.gebietName}</td>
        <td data-label="Vergleichbarkeit">{g.skalaWert !== null ? `${g.skalaWert}/5` : 'offen'}</td>
        <td data-label="Standorte">
          {g.standorte}
          <span className="zelle-hinweis">{g.mitKennwert} mit Kennwert</span>
        </td>
        <td data-label="niedrigstes" className="zahl">
          {g.minCent !== null ? euro(g.minCent) : '–'}
        </td>
        <td data-label="Median" className="zahl">
          {g.medianCent !== null ? euro(g.medianCent) : '–'}
        </td>
        <td data-label="höchstes" className="zahl">
          {g.maxCent !== null ? euro(g.maxCent) : '–'}
        </td>
        <td>
          <button type="button" className="knopf-klein-text" onClick={umschalten} aria-expanded={auf} aria-label={`Standorte in ${g.gebietName} ${auf ? 'ausblenden' : 'anzeigen'}`}>
            {auf ? 'Standorte ausblenden' : 'Standorte anzeigen'}
          </button>
        </td>
      </tr>
      {auf && (
        <tr className="benchmark-detail">
          <td colSpan={7}>
            <table className="tabelle tabelle-klein">
              <caption className="sr-only">Standorte in {g.gebietName}</caption>
              <thead>
                <tr>
                  <th scope="col">Standort</th>
                  <th scope="col">Kennwert</th>
                  <th scope="col">Abweichung vom Median</th>
                  <th scope="col">Reife</th>
                  <th scope="col">GWB nicht verjährt ab</th>
                  <th scope="col">BGB nicht verjährt ab</th>
                  <th scope="col"></th>
                </tr>
              </thead>
              <tbody>
                {g.eintraege.map((e) => (
                  <tr key={e.id}>
                    <td>
                      <Link to={`/standorte/${e.id}`}>{e.name}</Link>
                      <span className="zelle-hinweis">{[e.externeId, e.ort].filter(Boolean).join(' · ')}</span>
                    </td>
                    <td className="zahl">{e.kennwertCent !== null ? euro(e.kennwertCent) : '–'}</td>
                    <td className="zahl">{e.abweichungProzent !== null ? `${e.abweichungProzent > 0 ? '+' : ''}${e.abweichungProzent.toLocaleString('de-DE')} %` : '–'}</td>
                    <td>
                      <span className={`reife reife-${e.reife}`}>{REIFE_TEXT[e.reife]}</span>
                    </td>
                    <td>{e.gwbOffenAb ? monatText(e.gwbOffenAb) : 'kein Monat'}</td>
                    <td>{e.bgbOffenAb ? monatText(e.bgbOffenAb) : 'kein Monat'}</td>
                    <td>
                      <Link to={`/standorte/${e.id}/bericht`}>Bericht</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </td>
        </tr>
      )}
    </>
  );
}
